export type NarratorVoiceProfileId = 'clean' | 'radio' | 'cinematic';

type NarratorVoiceProfile = {
  id: NarratorVoiceProfileId;
  label: string;
  // EQ
  hpHz: number;
  lowShelfHz: number;
  lowShelfDb: number;
  mudHz: number;
  mudQ: number;
  mudDb: number;
  presenceLowHz: number;
  presenceLowQ: number;
  presenceLowDb: number;
  presenceHiHz: number;
  presenceHiQ: number;
  presenceHiDb: number;
  deEssHz: number;
  deEssQ: number;
  deEssDb: number;
  airHz: number;
  airDb: number;
  // Dynamics
  satDrive: number;
  compThreshold: number;
  compKnee: number;
  compRatio: number;
  compAttack: number;
  compRelease: number;
  limiterThreshold: number;
  limiterKnee: number;
  limiterRatio: number;
  limiterAttack: number;
  limiterRelease: number;
  makeup: number;
  // Loudness matching target (rough RMS dBFS approximation).
  targetDb: number;
  minGain: number;
  maxGain: number;
};

export const NARRATOR_VOICE_PROFILES: Record<NarratorVoiceProfileId, NarratorVoiceProfile> = {
  clean: {
    id: 'clean',
    label: 'Clean',
    hpHz: 68,
    lowShelfHz: 220,
    lowShelfDb: -1.0,
    mudHz: 340,
    mudQ: 1.0,
    mudDb: -1.6,
    presenceLowHz: 2500,
    presenceLowQ: 0.9,
    presenceLowDb: 1.4,
    presenceHiHz: 4100,
    presenceHiQ: 1.1,
    presenceHiDb: 1.0,
    deEssHz: 6400,
    deEssQ: 2.8,
    deEssDb: -2.0,
    airHz: 8800,
    airDb: 0.9,
    satDrive: 1.06,
    compThreshold: -18,
    compKnee: 12,
    compRatio: 2.2,
    compAttack: 0.010,
    compRelease: 0.16,
    limiterThreshold: -1.0,
    limiterKnee: 2,
    limiterRatio: 8,
    limiterAttack: 0.002,
    limiterRelease: 0.09,
    makeup: 1.14,
    targetDb: -18.0,
    minGain: 0.58,
    maxGain: 1.35,
  },
  radio: {
    id: 'radio',
    label: 'Radio',
    hpHz: 120,
    lowShelfHz: 250,
    lowShelfDb: -4.2,
    mudHz: 420,
    mudQ: 1.2,
    mudDb: -3.0,
    presenceLowHz: 2800,
    presenceLowQ: 1.1,
    presenceLowDb: 3.0,
    presenceHiHz: 4700,
    presenceHiQ: 1.3,
    presenceHiDb: 2.2,
    deEssHz: 6800,
    deEssQ: 3.4,
    deEssDb: -3.2,
    airHz: 9500,
    airDb: -1.0,
    satDrive: 1.18,
    compThreshold: -22,
    compKnee: 10,
    compRatio: 3.6,
    compAttack: 0.006,
    compRelease: 0.14,
    limiterThreshold: -1.8,
    limiterKnee: 1,
    limiterRatio: 12,
    limiterAttack: 0.001,
    limiterRelease: 0.08,
    makeup: 1.08,
    targetDb: -19.5,
    minGain: 0.52,
    maxGain: 1.28,
  },
  cinematic: {
    id: 'cinematic',
    label: 'Cinematic',
    hpHz: 62,
    lowShelfHz: 190,
    lowShelfDb: 0.2,
    mudHz: 320,
    mudQ: 0.9,
    mudDb: -1.0,
    presenceLowHz: 2400,
    presenceLowQ: 0.85,
    presenceLowDb: 1.2,
    presenceHiHz: 3800,
    presenceHiQ: 0.95,
    presenceHiDb: 0.8,
    deEssHz: 6100,
    deEssQ: 2.4,
    deEssDb: -1.6,
    airHz: 8600,
    airDb: 1.1,
    satDrive: 1.04,
    compThreshold: -16,
    compKnee: 14,
    compRatio: 1.9,
    compAttack: 0.012,
    compRelease: 0.19,
    limiterThreshold: -0.7,
    limiterKnee: 3,
    limiterRatio: 6,
    limiterAttack: 0.003,
    limiterRelease: 0.10,
    makeup: 1.20,
    targetDb: -17.5,
    minGain: 0.60,
    maxGain: 1.40,
  },
};

function createSaturatorCurve(drive: number): Float32Array {
  const d = Math.max(1.0, drive);
  const curve = new Float32Array(1024);
  for (let i = 0; i < curve.length; i++) {
    const x = (i / (curve.length - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * d) / Math.tanh(d);
  }
  return curve;
}

export class NarratorVoiceBus {
  readonly ctx: AudioContext;
  readonly input: GainNode;
  readonly output: GainNode;
  private profileId: NarratorVoiceProfileId;

  private hp: BiquadFilterNode;
  private lowShelf: BiquadFilterNode;
  private mud: BiquadFilterNode;
  private presenceLow: BiquadFilterNode;
  private presenceHi: BiquadFilterNode;
  private deEss: BiquadFilterNode;
  private air: BiquadFilterNode;
  private sat: WaveShaperNode;
  private comp: DynamicsCompressorNode;
  private limiter: DynamicsCompressorNode;

  constructor(ctx: AudioContext, initialProfile: NarratorVoiceProfileId = 'clean') {
    this.ctx = ctx;
    this.profileId = initialProfile in NARRATOR_VOICE_PROFILES ? initialProfile : 'clean';

    this.input = ctx.createGain();
    this.output = ctx.createGain();

    this.hp = ctx.createBiquadFilter();
    this.hp.type = 'highpass';
    this.lowShelf = ctx.createBiquadFilter();
    this.lowShelf.type = 'lowshelf';
    this.mud = ctx.createBiquadFilter();
    this.mud.type = 'peaking';
    this.presenceLow = ctx.createBiquadFilter();
    this.presenceLow.type = 'peaking';
    this.presenceHi = ctx.createBiquadFilter();
    this.presenceHi.type = 'peaking';
    this.deEss = ctx.createBiquadFilter();
    this.deEss.type = 'peaking';
    this.air = ctx.createBiquadFilter();
    this.air.type = 'highshelf';
    this.sat = ctx.createWaveShaper();
    this.comp = ctx.createDynamicsCompressor();
    this.limiter = ctx.createDynamicsCompressor();

    this.input.connect(this.hp);
    this.hp.connect(this.lowShelf);
    this.lowShelf.connect(this.mud);
    this.mud.connect(this.presenceLow);
    this.presenceLow.connect(this.presenceHi);
    this.presenceHi.connect(this.deEss);
    this.deEss.connect(this.air);
    this.air.connect(this.sat);
    this.sat.connect(this.comp);
    this.comp.connect(this.limiter);
    this.limiter.connect(this.output);
    this.output.connect(ctx.destination);

    this.applyProfile(this.profileId);
  }

  getProfileId(): NarratorVoiceProfileId {
    return this.profileId;
  }

  setProfile(profileId: NarratorVoiceProfileId): void {
    if (!(profileId in NARRATOR_VOICE_PROFILES)) return;
    this.applyProfile(profileId);
  }

  getProfileLabel(): string {
    return NARRATOR_VOICE_PROFILES[this.profileId].label;
  }

  private applyProfile(profileId: NarratorVoiceProfileId): void {
    const p = NARRATOR_VOICE_PROFILES[profileId];
    this.profileId = profileId;

    this.hp.frequency.value = p.hpHz;
    this.hp.Q.value = 0.68;
    this.lowShelf.frequency.value = p.lowShelfHz;
    this.lowShelf.gain.value = p.lowShelfDb;

    this.mud.frequency.value = p.mudHz;
    this.mud.Q.value = p.mudQ;
    this.mud.gain.value = p.mudDb;

    this.presenceLow.frequency.value = p.presenceLowHz;
    this.presenceLow.Q.value = p.presenceLowQ;
    this.presenceLow.gain.value = p.presenceLowDb;

    this.presenceHi.frequency.value = p.presenceHiHz;
    this.presenceHi.Q.value = p.presenceHiQ;
    this.presenceHi.gain.value = p.presenceHiDb;

    this.deEss.frequency.value = p.deEssHz;
    this.deEss.Q.value = p.deEssQ;
    this.deEss.gain.value = p.deEssDb;

    this.air.frequency.value = p.airHz;
    this.air.gain.value = p.airDb;

    // Toolchain compatibility: DOM lib typed-array generics can differ across
    // TS versions (ArrayBuffer vs ArrayBufferLike). Runtime value is valid.
    this.sat.curve = createSaturatorCurve(p.satDrive) as any;
    this.sat.oversample = '2x';

    this.comp.threshold.value = p.compThreshold;
    this.comp.knee.value = p.compKnee;
    this.comp.ratio.value = p.compRatio;
    this.comp.attack.value = p.compAttack;
    this.comp.release.value = p.compRelease;

    this.limiter.threshold.value = p.limiterThreshold;
    this.limiter.knee.value = p.limiterKnee;
    this.limiter.ratio.value = p.limiterRatio;
    this.limiter.attack.value = p.limiterAttack;
    this.limiter.release.value = p.limiterRelease;

    this.output.gain.value = p.makeup;
  }

  suggestLineGainForBuffer(buffer: AudioBuffer, baseGain = 1): number {
    const p = NARRATOR_VOICE_PROFILES[this.profileId];
    let sumSquares = 0;
    let count = 0;
    let peak = 0;
    const stride = 2; // cheap downsample for analysis

    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < data.length; i += stride) {
        const s = data[i];
        const abs = Math.abs(s);
        if (abs > peak) peak = abs;
        sumSquares += s * s;
        count++;
      }
    }

    const rms = Math.sqrt(sumSquares / Math.max(1, count));
    const db = 20 * Math.log10(Math.max(1e-9, rms));
    let gain = baseGain * Math.pow(10, (p.targetDb - db) / 20);

    gain = Math.max(p.minGain, Math.min(p.maxGain, gain));

    // Peak safety guard to avoid clipped consonants after normalization.
    if (peak > 1e-4) {
      const safeGain = 0.98 / peak;
      gain = Math.min(gain, safeGain);
    }
    return Math.max(0.35, Math.min(1.45, gain));
  }

  async suggestLineGainForSource(src: string, baseGain = 1): Promise<number> {
    const res = await fetch(src, { cache: 'force-cache' });
    if (!res.ok) throw new Error(`voice source fetch failed: ${res.status}`);
    const arrayBuffer = await res.arrayBuffer();
    const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer.slice(0));
    return this.suggestLineGainForBuffer(audioBuffer, baseGain);
  }
}

export function createNarratorVoiceBus(
  ctx: AudioContext,
  initialProfile: NarratorVoiceProfileId = 'clean',
): NarratorVoiceBus {
  return new NarratorVoiceBus(ctx, initialProfile);
}

